import { ApiError, type ApiErrorResponse } from './api-error';
import { env } from '../env';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

interface RequestOptions {
  /** Query string parameters – values are safely encoded. */
  params?: Record<string, string | number | boolean | undefined>;
  /** JSON-serializable request body. */
  body?: unknown;
  /** AbortSignal for cancellation (e.g. from TanStack Query). */
  signal?: AbortSignal;
  /** Request timeout in milliseconds (default: 30 000). */
  timeout?: number;
  /** Extra headers to merge (e.g. CSRF token). */
  headers?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// CSRF token management
// ---------------------------------------------------------------------------

let csrfToken: string | null = null;

/**
 * Fetch a fresh CSRF token from the backend and cache it in memory.
 * Called automatically before state-changing requests.
 */
export async function fetchCsrfToken(signal?: AbortSignal): Promise<string> {
  const res = await fetch(`${env.VITE_API_BASE_URL}/api/v1/auth/csrf`, {
    method: 'GET',
    credentials: 'include',
    signal,
  });

  if (!res.ok) {
    throw new ApiError(res.status, 'CSRF_FETCH_FAILED', 'Failed to fetch CSRF token');
  }

  const json = (await res.json()) as { data: { token: string } };
  csrfToken = json.data.token;
  return csrfToken;
}

/** Clear the cached CSRF token (e.g. on logout). */
export function clearCsrfToken(): void {
  csrfToken = null;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function buildUrl(path: string, params?: Record<string, string | number | boolean | undefined>): string {
  const url = new URL(`${env.VITE_API_BASE_URL}${path}`);

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }
  }

  return url.toString();
}

function isMutationMethod(method: HttpMethod): boolean {
  return method !== 'GET';
}

async function parseErrorBody(res: Response): Promise<ApiError> {
  try {
    const json = (await res.json()) as ApiErrorResponse;
    return new ApiError(
      res.status,
      json.error?.code ?? 'UNKNOWN_ERROR',
      json.error?.message ?? res.statusText,
      json.error?.details ?? [],
    );
  } catch {
    // Non-JSON error response (e.g. HTML error page from a proxy).
    return new ApiError(res.status, 'UNKNOWN_ERROR', res.statusText);
  }
}

// ---------------------------------------------------------------------------
// Core request function
// ---------------------------------------------------------------------------

const DEFAULT_TIMEOUT_MS = 30_000;

async function request<T>(method: HttpMethod, path: string, options: RequestOptions = {}): Promise<T> {
  const { params, body, signal: externalSignal, timeout = DEFAULT_TIMEOUT_MS, headers: extraHeaders } = options;

  // --- CSRF: fetch token before mutations if we don't have one ---
  if (isMutationMethod(method) && !csrfToken) {
    await fetchCsrfToken(externalSignal);
  }

  // --- Timeout via AbortSignal ---
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), timeout);

  // Combine external signal with timeout signal
  const signal = externalSignal
    ? AbortSignal.any([externalSignal, timeoutController.signal])
    : timeoutController.signal;

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...extraHeaders,
  };

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (isMutationMethod(method) && csrfToken) {
    headers['x-csrf-token'] = csrfToken;
  }

  try {
    const res = await fetch(buildUrl(path, params), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'include',
      signal,
    });

    clearTimeout(timeoutId);

    if (!res.ok) {
      const error = await parseErrorBody(res);

      // If CSRF token expired, clear it so the next mutation refetches.
      if (res.status === 403 && error.code === 'CSRF_TOKEN_INVALID') {
        clearCsrfToken();
      }

      throw error;
    }

    // Handle 204 No Content or empty bodies
    const contentLength = res.headers.get('content-length');
    if (res.status === 204 || contentLength === '0') {
      return undefined as T;
    }

    const contentType = res.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      return (await res.json()) as T;
    }

    // Non-JSON success (shouldn't happen with our API, but handle gracefully)
    return undefined as T;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof ApiError) {
      throw error;
    }

    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError(0, 'REQUEST_ABORTED', 'Request was cancelled or timed out');
    }

    // Network error (DNS failure, CORS, offline, etc.)
    throw new ApiError(0, 'NETWORK_ERROR', 'Unable to reach the server. Please check your connection.');
  }
}

// ---------------------------------------------------------------------------
// Public API client
// ---------------------------------------------------------------------------

export const apiClient = {
  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>('GET', path, options);
  },

  post<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>('POST', path, options);
  },

  patch<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>('PATCH', path, options);
  },

  put<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>('PUT', path, options);
  },

  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>('DELETE', path, options);
  },
};
